import type { AdminAICriteriaSetCreateParams, AdminAICriteriaSetUpdateParams } from 'services/api/fields';
import { legacyLabelPayload } from 'services/api/legacyCriterionKey';

type CategoryLike = { id?: string; name: string; color?: string; order?: number };
type CriteriaLike = {
  id?: string;
  label: string;
  description: string;
  category: string;
  // Absent for a criteria under a brand-new category created in the same save -- the category row
  // (and its id) doesn't exist until the request is processed, so the backend falls back to resolving
  // the category by `category` (name) when `categoryId` isn't present.
  categoryId?: string;
  order: number;
};

export type CriteriaSetFormValues = {
  client: { id: string; name: string } | null;
  group: { id: string; name: string } | null;
  categories: CategoryLike[];
  aiCriteria: CriteriaLike[];
  hiddenAiCriteriaIds: string[];
};

function normalizeCriteria(values: CriteriaSetFormValues) {
  const categoryCounters: Record<string, number> = {};
  return values.aiCriteria.map((c) => {
    const idx = categoryCounters[c.category] ?? 0;
    categoryCounters[c.category] = idx + 1;
    return {
      ...(c.id && !c.id.startsWith('_temp_') ? { id: c.id } : {}),
      ...legacyLabelPayload(c.label),
      description: c.description,
      // `category` (name) is kept alongside `category_id` as a fallback: a criteria under a
      // brand-new category has no category_id yet, since that category row is created as part of
      // this same request. The backend resolves by category_id when present, else by name.
      category: c.category,
      ...(c.categoryId ? { category_id: c.categoryId } : {}),
      order: idx,
    };
  });
}

function normalizeCategories(values: CriteriaSetFormValues) {
  return values.categories.map((c) => ({
    ...(c.id ? { id: c.id } : {}),
    name: c.name,
    color: c.color ?? '',
    order: c.order ?? 0,
  }));
}

export function buildCriteriaSetPayload(
  values: CriteriaSetFormValues,
  opts: { isEditing: boolean }
): AdminAICriteriaSetCreateParams | AdminAICriteriaSetUpdateParams {
  const criteria = normalizeCriteria(values);
  const categories = normalizeCategories(values);
  const isGroupScoped = !!values.group;

  if (opts.isEditing) {
    return {
      criteria,
      categories,
      ...(isGroupScoped ? { hidden_ai_criteria_ids: values.hiddenAiCriteriaIds } : {}),
    } as AdminAICriteriaSetUpdateParams;
  }

  return {
    type: 'admission',
    scopeable_type: isGroupScoped ? 'Group' : 'Client',
    scopeable_id: isGroupScoped ? values.group!.id : values.client!.id,
    criteria,
    categories,
    ...(isGroupScoped ? { hidden_ai_criteria_ids: values.hiddenAiCriteriaIds } : {}),
  } as AdminAICriteriaSetCreateParams;
}

// A brand-new group-scoped set that has no own criteria and hides none of the inherited
// criteria is a no-op: the group already inherits the client's full list without a set of its
// own, so saving one that changes nothing just adds clutter. Editing an existing set, and a
// new client-scoped set (no group selected), are unaffected.
export function isMeaninglessNewGroupSet(
  values: { group: { id: string } | null; aiCriteria: unknown[]; hiddenAiCriteriaIds: unknown[] },
  opts: { isEditing: boolean }
): boolean {
  if (opts.isEditing) return false;
  if (!values.group) return false;
  return values.aiCriteria.length === 0 && values.hiddenAiCriteriaIds.length === 0;
}

export function categoryNamesForDupGuard(
  ownCategories: { name: string }[],
  inheritedCategories: { name: string }[]
): string[] {
  return Array.from(new Set([...ownCategories, ...inheritedCategories].map((c) => c.name)));
}
